// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { CapsuleButton } = require('client-v2/common/components/buttons')
const { FetchStateEnum } = require('client-v2/common/utils/enums')
const EmojiMentionsInput = require('client-v2/common/components/emoji-mentions-input-lazy')
const Icon = require('client-v2/common/containers/icons/component')
const FollowPeopleSocialAuthCard = require('client-v2/logged-in-landing/containers/follow-people-social-auth-card/component-smart')

const bem = BemEntity('ShareModalV2ShareFriends')

const MAX_COMMENT_LENGTH = 200

function ShareModalShareFriends(props) {
  const { friends, friendsState, isSocialAuthCardVisible, submitState, user } = props
  const isSubmitted = submitState !== FetchStateEnum.EMPTY

  return (
    tag('div', {
      className: classNames(
        props.className,
        bem.block()
      )
    },
      tag('div', {
        className: bem.element('inner', {
          friends: friends.length !== 0
        })
      },
        tag(ShareModalShareFriendsList, {
          disabled: isSubmitted,
          friends,
          isLoading: friendsState === FetchStateEnum.LOADING,
          isSocialAuthCardVisible,
          onFriendClick: props.onFriendClick
        })
      ),
      tag(ShareModalShareFriendsMessageForm, {
        friends,
        submitState,
        user,
        onSubmit: props.onFriendsShare
      })
    )
  )
}

module.exports = ShareModalShareFriends

// BEM Block entity Element components

function ShareModalShareFriendsHeader(props) {
  const { leftScrollDisabled, rightScrollDisabled, onScrollClick } = props
  const handleScrollLeftClick = React.useCallback(
    () => onScrollClick(-300),
    [ onScrollClick ]
  )
  const handleScrollRightClick = React.useCallback(
    () => onScrollClick(300),
    [ onScrollClick ]
  )
  return (
    tag('div', {
      className: bem.element('friends-header')
    },
      tag('div', {
        className: bem.element('friends-label')
      },
        'Send to friends'
      ),
      tag('div', {
        className: bem.element('friends-scroll')
      },
        tag(Icon, {
          className: bem.element('friends-previous', {
            disabled: leftScrollDisabled
          }),
          id: 'previous',
          type: 'button',
          onClick: handleScrollLeftClick
        }),
        tag(Icon, {
          className: bem.element('friends-next', {
            disabled: rightScrollDisabled
          }),
          id: 'next',
          type: 'button',
          onClick: handleScrollRightClick
        })
      )
    )
  )
}

function ShareModalShareFriendsList(props) {
  const { friends, isLoading } = props
  const recalculateScrollWidth = !isLoading && friends.length

  const scrollRef = React.useRef()
  const [ isScrolledFullyLeft, setScrolledFullyLeft ] = React.useState(true)
  const [ isScrolledFullyRight, setScrolledFullyRight ] = React.useState(false)

  const handleScroll = React.useCallback(
    () => {
      const scrollEl = scrollRef.current
      if (scrollEl) {
        const { offsetWidth, scrollLeft, scrollWidth } = scrollEl
        setScrolledFullyLeft(scrollLeft <= 0)
        setScrolledFullyRight(scrollLeft + offsetWidth >= scrollWidth)
      }
    },
    [ setScrolledFullyLeft, setScrolledFullyRight ]
  )

  React.useEffect(
    () => {
      const scrollEl = scrollRef.current
      if (scrollEl && recalculateScrollWidth) {
        handleScroll()
      }
    },
    [ scrollRef, recalculateScrollWidth, handleScroll ]
  )

  const onScrollClick = React.useCallback(
    (distance) => {
      const scrollEl = scrollRef.current
      if (scrollEl) {
        scrollEl.scrollBy({
          left: distance,
          behavior: 'smooth'
        })
      }
    },
    [ scrollRef ]
  )

  if (isLoading) {
    return (
      tag('div', {
        className: bem.element('loading-indicator')
      })
    )
  }

  if (!friends.length) {
    return tag([
      tag(ShareModalShareFriendsHeader, {
        rightScrollDisabled: true,
        leftScrollDisabled: true,
        onScrollClick
      }),
      !props.isSocialAuthCardVisible && tag('div', {
        className: bem.element('empty-message')
      },
        tag('div', {
          className: bem.element('empty-title')
        },
          `Nobody's here yet.`
        ),
        tag('div', {
          className: bem.element('empty-subtitle')
        },
          `You can share directly with friends you are following who follow you back.`
        )
      ),
      props.isSocialAuthCardVisible && tag(FollowPeopleSocialAuthCard)
    ])
  }

  return (
    tag('div', {
      className: bem.element('friends-container')
    },
      tag(ShareModalShareFriendsHeader, {
        rightScrollDisabled: isScrolledFullyRight,
        leftScrollDisabled: isScrolledFullyLeft,
        onScrollClick
      }),
      tag('div', {
        ref: scrollRef,
        className: bem.element('friends-list'),
        onScroll: handleScroll
      },
        friends.map(friend => (
          tag(ShareModalShareFriendsItem, {
            key: `share-friend:${friend.id}`,
            disabled: props.disabled,
            friend,
            onClick: props.onFriendClick
          })
        ))
      )
    )
  )
}

function ShareModalShareFriendsItem(props) {
  const { friend } = props
  return (
    tag('div', {
      className: bem.element('friend', {
        selected: friend.isSelected
      }),
      onClick: () => props.onClick(friend.id)
    },
      tag(UserAvatar, {
        className: bem.element('friend-icon'),
        badge: false,
        href: true,
        title: friend.username,
        user: friend
    }),
      tag('div', {
        className: bem.element('friend-name')
      },
        `@${friend.username}`
      )
    )
  )
}

function ShareModalShareFriendsMessageForm(props) {
  const { onSubmit, submitState } = props
  const [ messageText, setMessageText ] = React.useState('')

  const onReset = event => {
    event.preventDefault()
    setMessageText('')
  }

  const handleSubmit = event => {
    event.preventDefault()
    onSubmit(messageText)
    setMessageText('')
  }

  const onInputChange = (event, newValue, newPlainTextValue) => {
    setMessageText(newPlainTextValue)
  }

  const selectedFriends = props.friends.filter(f => f.isSelected)
  const selectedCount = selectedFriends.length

  if (selectedFriends.length === 0 || submitState === FetchStateEnum.LOADED) {
    return null
  }

  return (
    tag('form', {
      className: bem.element('form'),
      onReset,
      onSubmit: handleSubmit
    },
      tag(EmojiMentionsInput, {
        maxLength: MAX_COMMENT_LENGTH,
        placeholder: 'Add a private message...',
        popperPlacement: 'top',
        value: messageText,
        onChange: onInputChange
      }),
      tag(CapsuleButton, {
        className: bem.element('form-submit'),
        type: 'submit',
        disabled: selectedCount === 0,
        onClick: handleSubmit
      },
        'Send'
      )
    )
  )
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  ShareModalShareFriends.propTypes = {
    // Values
    className: PropTypes.string,
    friends: PropTypes.arrayOf(PropTypes.object).isRequired,
    friendsState: PropTypes.string.isRequired,
    submitState: PropTypes.string.isRequired,
    user: PropTypes.object.isRequired,
    // Callbacks
    onFriendClick: PropTypes.func.isRequired,
    onFriendsShare: PropTypes.func.isRequired,
    onNavigate: PropTypes.func.isRequired
  }

  ShareModalShareFriendsList.propTypes = {
    // Values
    disabled: PropTypes.bool.isRequired,
    friends: PropTypes.arrayOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool.isRequired,
    isSocialAuthCardVisible: PropTypes.bool.isRequired,
    // Callbacks
    onFriendClick: PropTypes.func.isRequired
  }

  ShareModalShareFriendsItem.propTypes = {
    // Values
    disabled: PropTypes.bool,
    friend: PropTypes.object.isRequired,
    // Callbacks
    onClick: PropTypes.func.isRequired
  }


  ShareModalShareFriendsMessageForm.propTypes = {
    // Values
    friends: PropTypes.arrayOf(PropTypes.object).isRequired,
    user: PropTypes.object.isRequired,
    // Callbacks
    onSubmit: PropTypes.func.isRequired
  }
}
